import type { MusicalTimeline } from "@/lib/music/musical-timeline";
import { PianoRollRenderer, PianoRollRendererProps } from "@/lib/engine/components/piano-roll-renderer";
import { LaneStyle, NoteEntry } from "@/lib/engine/components/piano-roll/notes";
import type { ActivePress, NoteEffectStyle } from "@/lib/engine/components/piano-roll/note-effects/types";
import { PianoLayout } from "@/lib/piano/piano-layout";
import type { HorizontalProjection } from "@/lib/piano/horizontal-projection";
import { Piano } from "@/lib/piano/piano";
import { LocalTransport } from "@/lib/track/local-transport";
import { TrackManager } from "@/lib/track/track-manager";
import { Lane, type PlayedNote, type SustainSpan } from "@/lib/track/lane";
import { ringSeconds, trailEnd, type PedalTrail } from "@/lib/engine/components/piano-roll/pedal-span";
import { Container, FederatedPointerEvent, FederatedWheelEvent } from "pixi.js";
import { PianoComponent } from "./piano-component";
import { HintSelection } from "../hint-selection";
import { ViewNavigator } from "../view-navigator";
import { AnnotatedNote, NoteCluster } from "@/lib/music/part";
import { HandNotes, HintLabel, HintRow } from "@/lib/engine/components/piano-roll/harmony";
import { pitchNameWithOctave } from "@/lib/music/pitch-colors";
import { chordSymbolLabel, noteLabel } from "@/lib/music/notation";
import { getNotationStore } from "@/lib/music/notation-store";
import { currentKeyLabeler, keyLabelOf } from "@/lib/harmony/key-label";
import type { KeySpan } from "@/lib/harmony/key-spans";
import { readHandPalette, type HandPalette } from "@/lib/studio/theme/hand-palette";
import type { ColorToken } from "@/lib/studio/lane-view";
import { playheadDistanceFor } from "../playhead-distance";
import { nowLineOffsetPx } from "../now-line-offset";
import { ROLL_VISIBLE_SECONDS, rollSpeedRatio, rollTickSpan } from "../roll-horizon";
import { RULER_HIT_WIDTH, RULER_OPEN_WIDTH } from "@/lib/engine/components/piano-roll/measure-ruler";
import { RECORD_HANDLE_INSET, type RecordEdgeKind } from "@/lib/engine/components/piano-roll/record-marker";
import { EMPTY_LAYOUT, type SequenceLayout } from "../sequence-layout";

/** Default warm color used when a strike has no matching note of the piece under the playhead. */
const DEFAULT_PRESS_COLOR = 0xffb84d;

/**
 * Upper bound on the wall-clock delta handed to the effects (~4 frames at
 * 60 Hz). The renderer parks when idle and the tab can be backgrounded, so
 * the raw gap between two frames is not always a frame.
 */
const MAX_FRAME_DELTA_MS = 64;

/**
 * Durée de l'éclat qui répond à ton enfoncement de pédale, en millisecondes.
 * Assez court pour rester un geste et non un état : la tenue, c'est le ruban
 * qui la dit.
 */
const PEDAL_FLASH_MS = 320;

/** Rendu pour une lane sans pédale, plutôt qu'un tableau par lane et par image. */
const NO_SPANS: readonly SustainSpan[] = [];

/**
 * La plus longue extinction du clavier, en secondes : celle du la0. C'est de
 * cette distance qu'on remonte sous la fenêtre pour trouver les notes qui y
 * résonnent encore. Voir `ringSeconds`.
 */
const LONGEST_RING_SECONDS = 25;

/**
 * How far ahead (seconds) the auto-follow looks to recentre the view before
 * notes reach the playhead, so the next pitches are visible as they fall.
 */
const FOLLOW_LOOKAHEAD_SEC = 1.5;

export type ColorTokenResolver = (lane: Lane) => ColorToken;

/**
 * What the live-piano lane is, on this screen.
 *
 * The lane is cumulative by design: in free play, watching what you have just
 * played fall away under the playhead **is** the product, so `'notes'` is the
 * default and nothing else has ever been needed.
 *
 * The learning journey needs the other two. While a passage is being worked,
 * the player's own notes are noise on top of the notation they are reading —
 * and they are noise in a grey that sits at 1.03:1 against the left hand, so
 * they do not read as "mine" either. `'hidden'` is that state, and it is what
 * the journey holds almost all of the time.
 *
 * `'trace'` is the one moment it comes back: after a pass, on demand, drawn
 * as a contour over the written notes instead of beside them. Not a
 * visibility toggle with a different colour — a different object.
 */
export type LiveLaneMode = 'notes' | 'hidden' | 'trace';

/**
 * Tolérance verticale (px) pour reprendre une capsule au lieu de poser l'autre
 * borne. La capsule fait 8 px de haut ; le doigt, lui, ne vise pas au pixel.
 */
const EDGE_GRAB_PX = 12;

/**
 * Combien il faut s'éloigner du panneau ouvert pour jeter une borne.
 *
 * Assez pour qu'une dérive latérale pendant un glissé vertical ne jette rien,
 * assez peu pour que le geste tienne dans le même mouvement : la capsule suit
 * le doigt dans l'axe, on la voit sortir, et elle pâlit une fois dehors.
 */
const DISCARD_MARGIN_PX = 36;

/** Fallback cycle for orchestral parts (default token, beyond LH/RH). */
const EXTRA_TRACK_HEX = [0xffb84d, 0xff7af6, 0x39ffb6, 0x4d7cff];

/**
 * La zone d'enregistrement, en ticks.
 *
 * `endTick` est `null` entre le premier appui, qui pose le début, et le
 * second, qui pose la fin : une zone ouverte par le bas est un état que le
 * joueur voit à l'écran, pas une donnée invalide.
 */
export type RecordZone = {
  startTick: number;
  endTick: number | null;
};

type PianoRollComponentConfig = {
  /**
   * Ce que le rouleau montre, en secondes de musique au tempo de référence du
   * morceau. Converti en une hauteur de ticks par grille — et non par image :
   * voir {@link rollTickSpan}.
   */
  visibleDuration?: number;
};

/**
 * PianoRollComponent: coordinator for the piano roll.
 *
 * Owns the temporal window calculation and note collection each frame,
 * then hands pre-computed data to PianoRollRenderer (pure renderer).
 */
export class PianoRollComponent {
  private renderer?: PianoRollRenderer;
  private unsubscribeTrackManager?: () => void;
  private unsubscribeTransport?: () => void;

  /** Unsubscribe handles for per-track onChange listeners */
  private trackUnsubscribes: (() => void)[] = [];
  /** Unsubscribe handles for per-lane clusterChange listeners */
  private laneUnsubscribes: (() => void)[] = [];

  private lanes: Lane[] = [];
  /**
   * Pour chaque lane, le `writeCount` au dernier saut de la tête (molette,
   * boucle). Les frappes écrites avant sont une trace qu'on relit, voir
   * `playedThisPass`.
   */
  private readonly passStart = new WeakMap<Lane, number>();
  /** Stable lane index for coloring, assigned once per syncLanes(), survives hide/show */
  private laneStableIndices: Map<Lane, number> = new Map();
  /**
   * Les lanes des morceaux hors scène de la séquence, indexées à la suite de
   * celles en scène : les tampons de couleur les tiennent toutes par indice,
   * et aucune de celles en scène n'a à bouger. Vide sans séquence. Voir
   * `syncOffstage`.
   */
  private offstageIndices: Map<Lane, number> = new Map();
  private width = 0;
  private height = 0;

  private visibleDuration: number;
  /**
   * La hauteur du rouleau, en ticks : son échelle.
   *
   * Posée une fois par grille, et non mesurée en secondes à chaque image —
   * sans quoi un changement de tempo étire tout ce qui est à l'écran au lieu
   * de ralentir le défilement. Voir {@link rollTickSpan}.
   */
  private visibleTickSpan: number;
  /**
   * Distance courante (px) entre la barre de playhead et le bas du roll.
   * 0 = collée au piano. Au-dessus le futur, au-dessous le passé.
   *
   * Dérivée de la hauteur du roll à chaque `setSize`, voir
   * {@link playheadDistanceFor}. Amorcée à 0 comme `height` : le premier
   * layout la calcule avant la première frame.
   */
  private playheadDistancePx = 0;
  /**
   * Hauteur (px) déjà occupée au bas du roll, que la playhead doit surplomber.
   *
   * Posée depuis React — c'est le DOM qui la mesure, la caméra des mains d'un
   * replay étant un élément HTML posé sur le canvas. Voir
   * {@link playheadDistanceFor}.
   */
  private footReservePx = 0;
  private hintSelection?: HintSelection;

  private highlightActiveBeat = false;
  private workRange: { startTick: number; endTick: number } | null = null;
  private liveLaneMode: LiveLaneMode = 'notes';

  // ── Hint row cache ──────────────────────────────────────────────
  private cachedHintRows: HintRow[] = [];
  private cachedHintFromTick = NaN;
  private cachedHintToTick = NaN;
  private hintCacheDirty = true;

  /** The key spans of the piece, empty while it stays in its key. */
  private keySpans: readonly KeySpan[] = [];

  // ── Timeline change detection ──────────────────────────────────
  private currentTimeline: import("@/lib/music/musical-timeline").MusicalTimeline;
  /** Liée à `currentTimeline`, refaite avec elle et non à chaque image. */
  private tickToSecondsFn: (tick: number) => number;

  // ── Change detection (skip draw when nothing moved) ─────────
  private lastDrawnTick = NaN;
  private lastDrawnWidth = 0;
  private lastDrawnHeight = 0;
  private lastDrawnLaneCount = 0;
  private lastDrawnHighlightActiveBeat = false;
  /** Last horizontal offset drawn; a pan while paused must still repaint. */
  private lastDrawnOffsetX = NaN;

  // ── Pre-allocated per-frame buffers (avoids GC churn) ─────────
  private entriesBuffer: NoteEntry[] = [];
  /**
   * Les objets d'entrée, réutilisés : `entriesBuffer` est vidé à chaque image
   * recalculée, mais ce qu'il contenait reste ici et se remplit en place.
   * Sans ce bassin, chaque note visible coûtait un objet par image.
   */
  private readonly entryPool: NoteEntry[] = [];
  private laneColorsBuffer: LaneStyle[] = [];
  /**
   * Par indice de lane, comme `laneColorsBuffer` : la lane est-elle un piano
   * joué (le sien, un partenaire) plutôt qu'un morceau ?
   */
  private pianoLanesBuffer: boolean[] = [];
  /**
   * Les touches que les notes en train de passer font sonner, réutilisée
   * d'une image sur l'autre : une carte neuve à chaque image, soixante fois
   * par seconde, ne servirait qu'à occuper le ramasse-miettes.
   */
  private readonly soundingHints = new Map<number, number>();
  /** Les notes qui sonnent encore, relevées à chaque image. */
  private trailsBuffer: PedalTrail[] = [];
  /** Les objets de traîne, réutilisés comme ceux des notes. */
  private trailPool: PedalTrail[] = [];
  /** Par note MIDI, la traîne encore ouverte de la pédale en cours de relevé. */
  private readonly ringingByMidi = new Map<number, number>();
  /** Instant du dernier enfoncement de ta pédale, pour l'éclat. */
  private pedalPressedAtMS = -Infinity;
  private unsubscribePedal?: () => void;
  /** Lanes deferred to the end of `entriesBuffer`; reused, never reallocated. */
  private traceLanesBuffer: Lane[] = [];
  private pressesBuffer: ActivePress[] = [];
  private drawProps: PianoRollRendererProps = {
    fromTick: 0,
    toTick: 0,
    currentTick: 0,
    currentSeconds: 0,
    tickToSeconds: () => 0,
    playheadY: 0,
    entries: [],
    hints: [],
    keySpans: [],
    keyLabelOf,
    width: 0,
    height: 0,
    highlightActiveBeat: false,
    rulerExpanded: false,
    recordStartTick: null,
    recordStartBar: 1,
    recordStartBeat: 1,
    recordStartOffBeat: false,
    recordEndTick: null,
    recordEndBar: 1,
    recordEndBeat: 1,
    recordEndOffBeat: false,
    recordDragging: null,
    recordDiscardProgress: 0,
    recordDragX: null,
    workRange: null,
    songBoundaries: EMPTY_LAYOUT.boundaries(),
    sequenceVersion: 0,
    laneColors: [],
    pedalTrails: [],
    pedalWritten: NO_SPANS,
    pedalPlayed: NO_SPANS,
    pedalFlash: 0,
    presses: [],
    deltaMS: 0,
    rollSpeedRatio: 1,
    offsetX: 0,
  };

  /** Last frame timestamp for wall-clock delta computation. */
  private lastFrameMS = 0;

  /** Hand-color palette (read once from CSS at construction). */
  private palette: HandPalette;
  /** Counter used to assign extra-track colors to lanes whose token is `default`. */
  private extraColorIdxByLane = new Map<Lane, number>();

  // ── Navigation state ──────────────────────────────────────────
  private viewNavigator: ViewNavigator;
  private isDragging = false;
  private lastPointerY = 0;
  private lastPointerX = 0;
  /** Tick range visible in the current frame (for pixel→tick conversion) */
  private currentVisibleTickRange = 0;

  /** Reused buffer for the auto-follow pitch range (avoids per-frame alloc). */
  private readonly followRange = { min: 0, max: 0 };

  // ── Règle graduée et repère d'enregistrement ──────────────
  /**
   * La règle est-elle ouverte ?
   *
   * Vrai tant que la souris survole sa gouttière, et tant qu'un doigt y reste
   * posé. Voir `MeasureRuler` pour ce que l'ouverture montre, et pourquoi rien
   * n'est écrit au repos.
   */
  private rulerExpanded = false;
  /**
   * Un pointeur sans survol — doigt, stylet — est-il posé sur la règle ?
   *
   * Il n'y a pas de « sortie » à attendre d'un tel pointeur : la règle reste
   * ouverte jusqu'au relâchement.
   */
  private rulerHeld = false;
  /**
   * Les bornes de la zone d'enregistrement, calées sur des demi-temps.
   *
   * Vivent ici, en mémoire, et nulle part ailleurs : c'est un repérage de
   * séance. Publiées vers le studio par `onRecordZoneChange` pour qui voudra
   * les utiliser. La fin ne peut pas exister sans le début — c'est le premier
   * appui qui le pose — et les deux restent ordonnées, voir `applyEdge`.
   */
  private recordStartTick: number | null = null;
  private recordEndTick: number | null = null;
  /** La borne que le pointeur déplace en ce moment, s'il y en a une. */
  private draggingEdge: RecordEdgeKind | null = null;
  /**
   * À quel point la borne tenue est sortie de la règle, de 0 à 1.
   *
   * Elle pâlit à mesure, et à 1 le relâchement la jette : la pente *est*
   * l'avertissement, il n'y a pas de seuil invisible à franchir.
   */
  private discardProgress = 0;
  /** Abscisse du pointeur qui tient une borne ; la capsule la suit. */
  private edgeDragX: number | null = null;
  /**
   * Peut-on poser et déplacer les bornes dans la règle ?
   *
   * Éteint par défaut : la zone d'enregistrement n'a pas encore d'écran qui
   * s'en serve, et une règle qui pose un repère au premier appui surprendrait
   * un joueur qui voulait juste faire défiler le temps. Éteinte, la règle
   * s'ouvre et se lit toujours — c'est la pose qui est retirée, pas la règle —
   * et l'appui redevient ce qu'il est partout ailleurs sur le roll : un geste
   * de navigation. Une zone posée depuis l'extérieur reste dessinée.
   */
  private recordZoneEditable = false;
  private recordZoneListener?: (zone: RecordZone | null) => void;
  /**
   * La zone complète, sous la forme qu'attend le calque d'assombrissement.
   *
   * Reconstruite au déplacement d'une borne et pas à chaque image : le renderer
   * compare ses props par copie de surface, donc un objet réutilisé se
   * comparerait à lui-même et masquerait le changement. Ici l'identité change
   * exactement quand le contenu change, ce qui est la propriété dont dépend
   * cette comparaison.
   */
  private recordRange: { startTick: number; endTick: number } | null = null;

  // ── Cached frame geometry for hit-testing ──────────────────
  private lastFromTick = 0;
  private lastToTick = 0;

  constructor(
    private stage: Container,
    private pianoComponent: PianoComponent,
    private layout: PianoLayout,
    private localTransport: LocalTransport,
    private trackManager: TrackManager,
    viewNavigator: ViewNavigator,
    private getColorToken: ColorTokenResolver,
    private piano: Piano,
    /**
     * La séquence de morceaux, vue depuis le rouleau. `EMPTY_LAYOUT` sans
     * séquence : rien hors scène, aucune frontière, et le rouleau dessine
     * exactement ce qu'il dessinait.
     */
    private sequence: SequenceLayout = EMPTY_LAYOUT,
    config?: PianoRollComponentConfig
  ) {
    this.viewNavigator = viewNavigator;
    this.visibleDuration = config?.visibleDuration ?? ROLL_VISIBLE_SECONDS;
    this.currentTimeline = this.localTransport.transport.timeline;
    this.tickToSecondsFn = (tick: number) => this.currentTimeline.tickToSeconds(tick);
    this.visibleTickSpan = this.spanFor(this.currentTimeline);
    this.palette = readHandPalette();

    this.renderer = new PianoRollRenderer(
      this.stage,
      this.layout,
      this.localTransport.transport.timeline,
      this.pianoComponent,
    );

    this.stage.eventMode = 'static';
    this.setupNavigation();

    this.unsubscribeTransport = this.localTransport.on((event) => {
      if (event.type === 'seek' || event.type === 'loop') {
        for (const lane of this.lanes) {
          this.passStart.set(lane, lane.writeCount);
        }
      }
    });

    // Ton pied, et lui seul : l'éclat répond à un geste, et la pédale d'un
    // morceau en ferait un à chaque changement d'harmonie.
    this.unsubscribePedal = this.piano.on((event) => {
      if (event.type === 'sustain_on' && event.origin === 'player') {
        this.pedalPressedAtMS = performance.now();
      }
    });

    this.build();
  }

  /**
   * L'éclat de l'enfoncement, de 1 à 0. Voir `PedalFlash`.
   *
   * Lu à chaque image plutôt qu'avancé par un minuteur : le roll ne rend pas
   * à cadence fixe, et un décompte par image se figerait dès que la scène
   * cesse de bouger — c'est-à-dire exactement quand on appuie sur la pédale
   * sans rien jouer d'autre. `isAnimating` tient le ticker éveillé le temps
   * que l'éclat s'éteigne.
   */
  private pedalFlash(): number {
    const age = performance.now() - this.pedalPressedAtMS;

    if (age < 0 || age >= PEDAL_FLASH_MS) {
      return 0;
    }

    return 1 - age / PEDAL_FLASH_MS;
  }

  setHintSelection(selection: HintSelection): void {
    this.hintSelection = selection;
  }

  /**
   * The passage the learning journey is working on, or `null`.
   *
   * Kept on the component rather than passed per frame because it changes at
   * the pace of a step, not of a tick.
   */
  setWorkRange(range: { startTick: number; endTick: number } | null): void {
    this.workRange = range;
  }

  setHighlightActiveBeat(enabled: boolean): void {
    this.highlightActiveBeat = enabled;
  }

  /**
   * Le délai de restitution du transport vient de changer.
   *
   * Le roll n'en reçoit pas la valeur — il la lit sur le transport à chaque
   * image, voir `playoutDelayPx` — mais il doit passer le bailout statique :
   * le transport n'émet rien à ce moment-là, à dessein, et sur un roll parqué
   * rien de la scène ne bouge. La ligne du présent resterait à l'ancienne
   * hauteur jusqu'au prochain mouvement. Même geste que `setLiveLaneMode`.
   */
  playoutDelayChanged(): void {
    this.lastDrawnTick = NaN;
    this.renderer?.invalidate();
  }

  /**
   * Le délai de restitution, converti en hauteur d'écran pour l'image en cours.
   *
   * Lu sur le transport local à chaque image, et non reçu à part : c'est le
   * même nombre qui fait courir la tête derrière le présent, et la ligne du
   * présent n'a pas d'autre entrée. Converti à chaque image plutôt qu'une fois
   * en pixels parce que la hauteur de la bande dépend du zoom et du tempo, qui
   * changent tous les deux sans que le délai bouge. Voir `nowLineOffsetPx`.
   */
  /**
   * La vitesse du rouleau à la tête de lecture, en part de sa vitesse
   * nominale — le tempo écrit sous la tête, pressé par la vitesse de lecture,
   * rapporté au tempo de référence.
   *
   * Lue à la tête plutôt que mesurée sur le défilement : un rouleau figé ne
   * doit pas rendre zéro, on y joue encore. Voir {@link rollSpeedRatio}.
   */
  private speedRatio(currentTick: number, timeline: MusicalTimeline): number {
    const ticksPerSecond =
      (timeline.getBpmAtTick(currentTick) / 60)
      * timeline.getPpq()
      * this.localTransport.getPlaybackRate();

    return rollSpeedRatio(ticksPerSecond, this.visibleTickSpan, this.visibleDuration);
  }

  /**
   * L'échelle du rouleau pour une grille donnée, en ticks.
   *
   * Le tempo de référence est celui du début — `getBpmAtTick(0)` rend
   * l'événement posé à l'origine, ou le tempo par défaut de la grille quand
   * elle n'en a pas, qui est justement celui avec lequel elle compte ses
   * premières secondes.
   */
  private spanFor(timeline: MusicalTimeline): number {
    return rollTickSpan(timeline.getPpq(), timeline.getBpmAtTick(0), this.visibleDuration);
  }

  private playoutDelayPx(fromTick: number, toTick: number, timeline: MusicalTimeline): number {
    const windowSeconds = timeline.tickToSeconds(toTick) - timeline.tickToSeconds(fromTick);

    return nowLineOffsetPx(this.localTransport.getPlayoutDelay(), windowSeconds, this.height);
  }

  /**
   * How the live-piano lane is drawn — see {@link LiveLaneMode}.
   *
   * Forces the next frame past the static bailout. That test asks whether the
   * *scene* moved (tick, size, visible lane count, offset), and none of those
   * change here: the roll would keep presenting the last frame and the trace
   * would appear only when the player happened to scroll.
   */
  setLiveLaneMode(mode: LiveLaneMode): void {
    if (mode === this.liveLaneMode) {
      return;
    }

    this.liveLaneMode = mode;
    this.lastDrawnTick = NaN;
    // Past the component's bailout is not enough: the renderer keeps its own,
    // and every prop it compares — the tick window, the size, the offset — is
    // unchanged when the roll is parked, which is exactly the state the trace
    // is turned off in.
    this.renderer?.invalidate();
    // The live lane's clusters feed the harmony rows too — "C oct" over what
    // the player is holding. Those follow the notes in and out.
    this.hintCacheDirty = true;
  }

  setNoteEffectStyle(style: NoteEffectStyle): void {
    this.renderer?.setNoteEffectStyle(style);
  }

  setNoteDust(enabled: boolean): void {
    this.renderer?.setNoteDust(enabled);
  }

  setMeasureLinesVisible(visible: boolean): void {
    // Le bailout du composant compare le tick, la taille et la trame : un roll
    // à l'arrêt ne repeindrait pas sans ce reset.
    this.lastDrawnTick = NaN;
    this.renderer?.setMeasureLinesVisible(visible);
  }

  /**
   * True while the roll still owes frames to something that animates on its
   * own clock: a key held down (its halo pulses and keeps emitting particles)
   * or particles still falling after the release.
   *
   * The engine governor polls this so the ticker never parks mid-effect. A
   * press only *wakes* the renderer once; without this probe the grace window
   * would run out under a key still held down and freeze the sparks in place.
   */
  isAnimating(): boolean {
    return this.pedalFlash() > 0
      || this.piano.getPressedMidis().size > 0
      || (this.renderer?.hasActiveEffects() ?? false)
      || (this.renderer?.isRulerAnimating() ?? false);
  }

  setSize(width: number, height: number) {
    this.width = width;
    this.height = height;
    this.playheadDistancePx = playheadDistanceFor(height, this.footReservePx);
    this.renderer?.setSize(width, height);
  }

  /**
   * Réserve une hauteur au bas du roll : la playhead remonte pour la
   * surplomber, et ce qui s'y pose n'est plus posé *sur* la ligne d'attaque
   * mais dessous.
   *
   * Mesurée par celui qui occupe la place, jamais devinée ici : c'est un
   * élément DOM, il connaît sa taille et elle change avec l'écran.
   */
  setFootReserve(px: number): void {
    const reserve = Math.max(0, Math.round(px));

    if (reserve === this.footReservePx) {
      return;
    }

    this.footReservePx = reserve;
    this.playheadDistancePx = playheadDistanceFor(this.height, reserve);
    // Rien n'a bougé de la scène : sans cela le rouleau garderait l'image
    // d'avant jusqu'à la prochaine note.
    this.lastDrawnTick = NaN;
    this.renderer?.invalidate();
  }

  /**
   * Allume au clavier les touches des notes que la ligne d'attaque est en
   * train de traverser, et éteint les autres.
   *
   * # Pourquoi c'est le rouleau qui le dit
   *
   * Lui seul tient déjà, à chaque image, les notes visibles et la couleur de
   * leur lane. Refaire ce relevé ailleurs voudrait dire relire le morceau, et
   * le faire dans React coûterait un rendu par note.
   *
   * # Ce que ça n'est pas
   *
   * Ce n'est pas une annonce : les touches s'allument **pendant** que la note
   * sonne, pas avant. Le clavier devient la lecture directe de ce qu'on
   * entend, et non un second rouleau qui dirait la suite.
   *
   * Ce n'est pas non plus une leçon : ces touches n'ont pas droit à leur nom
   * en pastille, au contraire de celles qu'un parcours désigne. Voir
   * `Piano.setSoundingHints`, qui tient les deux couches.
   *
   * Une note tenue (`held`) finit au tick courant et ne serait jamais prise
   * par une comparaison stricte : elle sonne pourtant, c'est même tout ce
   * qu'elle fait.
   */
  private updateSoundingHints(currentTick: number): void {
    const sounding = this.soundingHints;

    sounding.clear();

    for (const entry of this.entriesBuffer) {
      if (entry.tick > currentTick) {
        continue;
      }

      if (!entry.held && currentTick >= entry.noteEndTick) {
        continue;
      }

      const style = this.laneColorsBuffer[entry.laneIdx];

      if (style === undefined) {
        continue;
      }

      sounding.set(entry.playedNote.note.toMidi(), style.fill);
    }

    this.piano.setSoundingHints(sounding);
  }

  /**
   * Distance courante (px) entre la playhead et le bas du roll, telle que
   * réellement rendue. La vue la publie vers React après chaque layout, pour
   * que les overlays qui réservent un couloir de lecture au-dessus de la
   * playhead visent la bonne ligne au lieu d'en recopier une constante.
   */
  get playheadDistance(): number {
    return this.playheadDistancePx;
  }

  draw() {
    const now = performance.now();
    // Clamped: coming back from a parked ticker or a backgrounded tab, the
    // raw wall-clock gap can be seconds long, which would teleport every
    // particle instead of animating it. Cap it at a few frames' worth.
    const deltaMS = this.lastFrameMS === 0
      ? 16
      : Math.min(MAX_FRAME_DELTA_MS, Math.max(0, now - this.lastFrameMS));
    this.lastFrameMS = now;

    const currentTick = this.viewNavigator.getEffectiveTick();
    const timeline = this.localTransport.transport.timeline;

    // Detect timeline replacement (e.g. new song loaded with different time signature / PPQ)
    if (timeline !== this.currentTimeline) {
      this.currentTimeline = timeline;
      this.tickToSecondsFn = (tick: number) => timeline.tickToSeconds(tick);
      this.visibleTickSpan = this.spanFor(timeline);
      this.renderer?.setMusicalTimeline(timeline);
      // L'échelle vient de changer alors que rien de la scène n'a bougé : une
      // séance qui pose sa grille sur un rouleau en pause garderait l'ancienne
      // jusqu'au prochain mouvement.
      this.lastDrawnTick = NaN;
      this.renderer?.invalidate();
    }

    // Active inputs that may force a redraw / effect frame
    const pressedMidis = this.piano.getPressedMidis();
    const hasPresses = pressedMidis.size > 0;
    const effectsActive = this.renderer?.hasActiveEffects() ?? false;

    // Static bailout test: skip all heavy recomputation when the scene hasn't moved.
    // The horizontal offset is included so a manual pan while paused (tick frozen)
    // still triggers a repaint at the new scroll position.
    const currentOffsetX = this.pianoComponent.getOffsetX();
    let visibleLaneCount = 0;

    for (let i = 0; i < this.lanes.length; i++) {
      if (!this.lanes[i].isHidden()) {
        visibleLaneCount++;
      }
    }

    const staticUnchanged = (
      currentTick === this.lastDrawnTick
      && this.width === this.lastDrawnWidth
      && this.height === this.lastDrawnHeight
      && visibleLaneCount === this.lastDrawnLaneCount
      && this.highlightActiveBeat === this.lastDrawnHighlightActiveBeat
      && currentOffsetX === this.lastDrawnOffsetX
      && !this.hintCacheDirty
    );

    // Full bailout: nothing changed AND no animation in flight. L'ouverture de
    // la règle en est une : elle bouge alors que rien de la scène ne bouge, et
    // c'est justement à l'arrêt qu'on va la chercher.
    const rulerAnimating = this.renderer?.isRulerAnimating() ?? false;

    if (staticUnchanged && !hasPresses && !effectsActive && !rulerAnimating) {
      return;
    }

    if (!staticUnchanged) {
      this.lastDrawnTick = currentTick;
      this.lastDrawnWidth = this.width;
      this.lastDrawnHeight = this.height;
      this.lastDrawnLaneCount = visibleLaneCount;
      this.lastDrawnHighlightActiveBeat = this.highlightActiveBeat;
      this.lastDrawnOffsetX = currentOffsetX;

      // L'échelle du rouleau, en ticks : `visibleDuration` secondes au tempo de
      // référence du morceau, posée une fois pour toutes par `spanFor`. Elle ne
      // se remesure pas au tempo courant — un changement de tempo ralentit ou
      // accélère le défilement, il n'étire pas le rouleau. Voir `rollTickSpan`.
      const currentSeconds = timeline.tickToSeconds(currentTick);
      const visibleTickRange = this.visibleTickSpan;

      this.currentVisibleTickRange = visibleTickRange;

      // When the playhead is raised above the bottom, shift the visible tick
      // window so the playhead bar always maps to currentTick.
      const distancePx = this.playheadDistancePx;
      const pastTicks = this.height > 0
        ? (distancePx / this.height) * visibleTickRange
        : 0;

      const fromTick = currentTick - pastTicks;
      const toTick = currentTick + visibleTickRange - pastTicks;
      const playheadY = this.height - distancePx;

      this.lastFromTick = fromTick;
      this.lastToTick = toTick;

      // Reuse pre-allocated entries buffer, reset length instead of allocating
      const entries = this.entriesBuffer;
      entries.length = 0;

      const laneColors = this.laneColorsBuffer;
      laneColors.length = 0;

      const pianoLanes = this.pianoLanesBuffer;
      pianoLanes.length = 0;

      const traceLanes = this.traceLanesBuffer;
      traceLanes.length = 0;

      const trails = this.trailsBuffer;
      trails.length = 0;

      // Ticks par seconde, pour convertir l'extinction d'une corde — qui est
      // une durée physique — en ticks, qui sont une durée musicale. Lu sur la
      // fenêtre : un changement de tempo à l'intérieur ne vaut pas une
      // seconde conversion par note.
      const windowSeconds = this.tickToSecondsFn(toTick) - this.tickToSecondsFn(fromTick);
      const ticksPerSecond = windowSeconds > 0 ? (toTick - fromTick) / windowSeconds : 0;

      // Les deux rubans du bord. Le morceau parle par n'importe laquelle de
      // ses lanes — un piano n'a qu'un jeu d'étouffoirs, et `SongControler`
      // écrit le même pédalage dans toutes.
      //
      // Le ruban joué, lui, a deux candidats quand on regarde quelqu'un : le
      // sien et le nôtre. Voir `pedalPlayedRoom`.
      let pedalWritten: readonly SustainSpan[] = NO_SPANS;
      let pedalPlayedMine: readonly SustainSpan[] = NO_SPANS;
      let pedalPlayedRoom: readonly SustainSpan[] = NO_SPANS;

      for (let i = 0; i < this.lanes.length; i++) {
        const lane = this.lanes[i];
        const laneIdx = this.laneStableIndices.get(lane) ?? i;
        const colorHex = this.resolveLaneHex(lane);
        const piano = this.isPianoLane(lane);
        const trace = this.isTraceLane(lane);

        while (laneColors.length <= laneIdx) {
          laneColors.push({ fill: colorHex, stroke: colorHex });
          pianoLanes.push(false);
        }

        laneColors[laneIdx] = {
          fill: colorHex,
          stroke: colorHex,
          outline: trace,
          dynamics: this.isSounding(lane),
        };
        pianoLanes[laneIdx] = piano;

        if (lane.isHidden()) {
          continue;
        }

        // Une pédale sans levée connue s'arrête au présent de sa lane — le
        // pied est encore en bas, et on ne sait rien après — et court jusqu'au
        // haut de la fenêtre sur un morceau, dont la pédale est écrite
        // d'avance. Voir `laneNow` : ce présent n'est pas la tête de lecture
        // pour tout le monde.
        const spans = lane.getSustainSpans(
          fromTick,
          toTick,
          piano ? this.laneNow(lane, currentTick) : toTick,
        );

        if (spans.length > 0) {
          this.collectTrails(lane, laneIdx, spans, fromTick, toTick, ticksPerSecond, trails);

          if (!piano) {
            if (pedalWritten === NO_SPANS) {
              pedalWritten = spans;
            }
          } else if (lane.hasSharedTimebase) {
            if (pedalPlayedRoom === NO_SPANS) {
              pedalPlayedRoom = spans;
            }
          } else {
            pedalPlayedMine = spans;
          }
        }

        // Deferred, not skipped. Inside `noteLayer` the z-order is pool
        // acquisition order, which is this loop's order, so a lane collected
        // here would be drawn *under* the notes it comments on. Behind them, a
        // strike that landed exactly right leaves no mark at all — and a
        // reading where only the misses are visible teaches the wrong thing.
        // In front, a perfect note carries a pale rim: it is the signature of
        // having got it right.
        if (trace) {
          if (this.liveLaneMode === 'trace') {
            traceLanes.push(lane);
          }

          continue;
        }

        this.collectLaneNotes(lane, laneIdx, fromTick, toTick, currentTick, entries);
      }

      for (const lane of traceLanes) {
        const laneIdx = this.laneStableIndices.get(lane) ?? 0;
        this.collectLaneNotes(lane, laneIdx, fromTick, toTick, currentTick, entries);
      }

      // Les morceaux hors scène de la séquence, chacun à son décalage. Après
      // les lanes en scène : ce sont des partitions dont rien ne sonne ici,
      // donc ni frappe, ni pédale, ni rangée d'accords — seulement des notes
      // à leur place. La liste est vide sans séquence, et c'est tout ce que
      // ce cas-là en coûte.
      for (const song of this.sequence.offstage()) {
        for (const lane of song.lanes) {
          const laneIdx = this.offstageIndices.get(lane);

          if (laneIdx === undefined) {
            continue;
          }

          const colorHex = this.resolveLaneHex(lane);

          while (laneColors.length <= laneIdx) {
            laneColors.push({ fill: colorHex, stroke: colorHex });
            pianoLanes.push(false);
          }

          laneColors[laneIdx] = { fill: colorHex, stroke: colorHex, dynamics: !lane.isMuted() };
          pianoLanes[laneIdx] = false;

          if (lane.isHidden()) {
            continue;
          }

          this.collectLaneNotes(lane, laneIdx, fromTick, toTick, currentTick, entries, song.offsetTicks);
        }
      }

      const props = this.drawProps;
      props.fromTick = fromTick;
      props.toTick = toTick;
      props.currentTick = currentTick;
      props.currentSeconds = currentSeconds;
      props.tickToSeconds = this.tickToSecondsFn;
      props.playheadY = playheadY;
      props.playoutDelayPx = this.playoutDelayPx(fromTick, toTick, timeline);
      props.entries = entries;
      props.hints = this.computeUnifiedRows(fromTick, toTick);
      props.keySpans = this.keySpans;
      // Relu à chaque image : c'est l'identité de la fonction qui dit à la
      // marge que la notation a changé (voir `currentKeyLabeler`).
      props.keyLabelOf = currentKeyLabeler();
      props.width = this.width;
      props.height = this.height;
      props.hintSelection = this.hintSelection;
      props.highlightActiveBeat = this.highlightActiveBeat;
      // L'extérieur de la zone d'enregistrement s'assombrit, avec le calque
      // du passage travaillé — c'est le même propos (« ce n'est pas de ça
      // qu'on parle »), et il n'y a aucune raison d'en écrire un second. Elle
      // passe devant la plage du parcours quand les deux existent : le
      // parcours pose la sienne au rythme d'une étape, la zone est le geste
      // que le joueur vient de faire.
      props.workRange = this.recordRange ?? this.workRange;
      props.workRangeRuled = this.recordRange === null;
      props.songBoundaries = this.sequence.boundaries();
      props.sequenceVersion = this.sequence.version;
      props.laneColors = laneColors;
      props.pedalTrails = trails;
      props.pedalWritten = pedalWritten;
      // Celui de la salle passe devant le nôtre : quand on regarde un
      // interprète, le rouleau entier est son jeu, et le ruban joué doit
      // parler de la même personne que les notes qui descendent. Le nôtre
      // reprend la bande dès qu'il n'y a personne à regarder — c'est-à-dire
      // hors séance, et pour l'interprète lui-même.
      props.pedalPlayed = pedalPlayedRoom !== NO_SPANS ? pedalPlayedRoom : pedalPlayedMine;

      // Auto-follow: while playing, pan the horizontal view ONLY when the
      // active/imminent pitch range leaves the frame (edge-triggered, minimal
      // movement). No-op when everything fits or while a recent manual pan
      // still suppresses it.
      if (!this.localTransport.isDetached && this.updateFollowRange(currentTick, currentSeconds)) {
        this.pianoComponent.autoFollowToRange(this.followRange.min, this.followRange.max, deltaMS);
      }
    }

    // Compute presses every frame (cheap) so the effects layer stays current.
    const presses = this.computePresses(pressedMidis, currentTick);

    // Update per-entry isHit so the Notes layer can highlight correctly-played notes.
    // Always recompute against the cached entriesBuffer, even on the static-bailout path.
    this.updateEntryHits(pressedMidis, currentTick);

    // Et les touches que ces notes font sonner, portées au clavier.
    this.updateSoundingHints(currentTick);

    const props = this.drawProps;
    props.deltaMS = deltaMS;
    // Posée hors du bailout, comme `deltaMS` : les effets tournent à chaque
    // image, même quand la scène ne bouge pas.
    props.rollSpeedRatio = this.speedRatio(currentTick, timeline);
    props.rulerExpanded = this.rulerExpanded;
    props.recordStartTick = this.recordStartTick;
    props.recordEndTick = this.recordEndTick;
    props.recordDragging = this.draggingEdge;
    props.recordDiscardProgress = this.discardProgress;
    props.recordDragX = this.edgeDragX;

    const startTick = this.recordStartTick === null ? null : Math.round(this.recordStartTick);
    props.recordStartBar = startTick === null ? 1 : timeline.getBarAtTick(startTick) + 1;
    props.recordStartBeat = startTick === null ? 1 : timeline.getBeatAtTick(startTick) + 1;
    props.recordStartOffBeat = startTick === null ? false : this.isOffBeat(startTick);

    const endTick = this.recordEndTick === null ? null : Math.round(this.recordEndTick);
    props.recordEndBar = endTick === null ? 1 : timeline.getBarAtTick(endTick) + 1;
    props.recordEndBeat = endTick === null ? 1 : timeline.getBeatAtTick(endTick) + 1;
    props.recordEndOffBeat = endTick === null ? false : this.isOffBeat(endTick);
    props.presses = presses;
    props.pedalFlash = this.pedalFlash();
    // Read after auto-follow so the renderer's redraw detection sees the
    // up-to-date offset (kept in sync with the keyboard, drawn just before).
    props.offsetX = this.pianoComponent.getOffsetX();

    this.renderer?.draw(props);
  }

  private updateEntryHits(
    pressedMidis: ReadonlyMap<number, number>,
    currentTick: number,
  ): void {
    const entries = this.entriesBuffer;

    if (pressedMidis.size === 0) {
      for (let i = 0; i < entries.length; i++) {
        entries[i].isHit = false;
      }

      return;
    }

    for (let i = 0; i < entries.length; i++) {
      const e = entries[i];
      const midi = e.playedNote.note.toMidi();
      const inWindow = e.tick <= currentTick && e.noteEndTick >= currentTick;

      e.isHit = inWindow && pressedMidis.has(midi);
    }
  }

  /**
   * Les frappes qui passent la tête de lecture, une par touche, projetées sur
   * le roll pour les halos.
   *
   * Tirées des notes de chaque lane qui sonne (`isSounding`) — le clavier, un
   * partenaire, un interprète, une partie du morceau — au moment où elles
   * traversent la ligne. Le halo suit la note qui tombe, et non plus les
   * touches du piano local : c'est ce qui l'allume sur le jeu d'un partenaire,
   * qui n'enfonce rien ici, et sur le morceau qu'on écoute.
   *
   * La lane du clavier est muette : relue après un saut de la tête, sa trace ne
   * sonne pas, et ne frappe donc rien. Seules y comptent les notes jouées
   * depuis (`playedThisPass`). Elle part quand même en paillettes sous la
   * ligne, ce n'est pas une frappe.
   *
   * Une touche du clavier qu'aucune lane n'a écrite s'allume, elle, tout de
   * suite, comme avant. Vue décrochée — à l'arrêt, ou remontée à la molette —,
   * c'est le cas de toutes : la lane du clavier n'écrit plus
   * (`PianoControler`), et une note ancienne sous la ligne figée n'est la
   * frappe de personne. En lecture, c'est celle qui a démarré le transport du
   * jeu libre, lue par la lane avant le démarrage. Sans quoi un essai au
   * clavier resterait sans réponse.
   */
  private computePresses(
    pressedMidis: ReadonlyMap<number, number>,
    currentTick: number,
  ): readonly ActivePress[] {
    const out = this.pressesBuffer;
    out.length = 0;

    const playheadY = this.height - this.playheadDistancePx;
    const detached = this.localTransport.isDetached;
    // Une projection neuve à chaque appel : prise à la première frappe
    // seulement, pas à chaque image où rien ne passe la ligne.
    let projection: HorizontalProjection | null = null;

    if (!detached) {
      for (const lane of this.lanes) {
        if (lane.isHidden() || !this.isSounding(lane)) {
          continue;
        }

        // Commencées avant la tête, et pas encore finies : tenues, ou
        // relâchées après elle. Une note posée pile sur la tête attend l'image
        // suivante.
        for (const playedNote of lane.getNotesInRange(currentTick, currentTick)) {
          if (lane.isMuted() && !this.playedThisPass(lane, playedNote)) {
            continue;
          }

          projection ??= this.pianoComponent.getProjection();
          this.pushPress(out, playedNote.note.toMidi(), playedNote.velocity, currentTick, projection, playheadY);
        }
      }
    }

    for (const [midi, velocity] of pressedMidis) {
      // Écrite, sa note allume le halo en passant la ligne : tout de suite en
      // solo, un délai de restitution plus tard chez un émetteur de séance.
      if (!detached && (out.some((press) => press.midi === midi) || this.keyboardLaneHolds(midi, currentTick))) {
        continue;
      }

      projection ??= this.pianoComponent.getProjection();
      this.pushPress(out, midi, velocity, currentTick, projection, playheadY);
    }

    return out;
  }

  /**
   * La lane du clavier tient-elle cette touche : écrite, et pas encore
   * relâchée ?
   *
   * La lane du clavier, et non celle d'un partenaire : c'est la seule lane de
   * piano qui ne vit pas sur le transport partagé. Cherchée jusqu'au tick de
   * capture seulement : un geste ne s'écrit jamais plus loin, et au-delà, sur
   * un jeu libre qu'on rejoue, il y aurait tout le reste de la séance à lire à
   * chaque image.
   */
  private keyboardLaneHolds(midi: number, currentTick: number): boolean {
    const captureTick = this.localTransport.captureTick();

    for (const lane of this.lanes) {
      if (lane.isHidden() || lane.hasSharedTimebase || !this.isPianoLane(lane)) {
        continue;
      }

      for (const playedNote of lane.getNotesInRange(currentTick, Math.max(currentTick, captureTick) + 1)) {
        if (playedNote.tickDuration === undefined && playedNote.note.toMidi() === midi) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Ajoute une frappe, ou renforce celle déjà posée sur la même touche : deux
   * pianos sur le même do n'allument qu'un halo, à la force du plus fort.
   */
  private pushPress(
    out: ActivePress[],
    midi: number,
    velocity: number,
    currentTick: number,
    projection: HorizontalProjection,
    playheadY: number,
  ): void {
    for (const press of out) {
      if (press.midi === midi) {
        press.velocity = Math.max(press.velocity, velocity);

        return;
      }
    }

    const keyLayout = this.layout.getKeyByMidi(midi);

    if (!keyLayout) {
      return;
    }

    const x = projection.keyCenter(keyLayout);
    const keyWidth = keyLayout.color === "white"
      ? projection.whiteKeyWidth
      : projection.blackKeyWidth;

    const color = this.findActiveLaneColor(midi, currentTick) ?? DEFAULT_PRESS_COLOR;

    out.push({ midi, x, y: playheadY, keyWidth, color, velocity });
  }

  /**
   * Find the lane fill color for the note of the piece matching `midi` at
   * `currentTick`, if any. Used so the halo / particles inherit the note's
   * color when a strike actually lands on a roll note.
   */
  private findActiveLaneColor(midi: number, currentTick: number): number | undefined {
    const entries = this.entriesBuffer;
    const laneColors = this.laneColorsBuffer;

    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];

      if (entry.playedNote.note.toMidi() !== midi) {
        continue;
      }

      if (entry.tick > currentTick || entry.noteEndTick < currentTick) {
        continue;
      }

      // The halo says "you are on a note of the piece". What a piano played —
      // the strike itself, a partner's, or the trace of a pass that is over —
      // is not one: a strike landing on it falls through to the default warm
      // colour like any other free strike.
      if (this.pianoLanesBuffer[entry.laneIdx] === true) {
        continue;
      }

      return laneColors[entry.laneIdx]?.fill;
    }

    return undefined;
  }

  /**
   * Whether this lane is played on a piano — the local keyboard, a session
   * partner, a recital performer — rather than a part of the piece.
   */
  private isPianoLane(lane: Lane): boolean {
    return this.getColorToken(lane) === 'live';
  }

  /**
   * Whether this lane is the trace rather than a part of the piece.
   *
   * Only the live piano can be one, and only when the journey has asked for
   * it: in `'notes'` the lane is an ordinary lane and nothing here applies.
   */
  private isTraceLane(lane: Lane): boolean {
    return this.liveLaneMode !== 'notes' && this.isPianoLane(lane);
  }

  /**
   * La lane sonne-t-elle : ses notes portent-elles halo et bande d'attaque ?
   *
   * Une lane de piano, toujours : celle du clavier est muette par construction
   * (`StudioSession`, le son sort du piano lui-même), et elle joue pourtant.
   * Une partie du morceau, tant qu'on ne l'a pas coupée.
   */
  private isSounding(lane: Lane): boolean {
    return this.isPianoLane(lane) || !lane.isMuted();
  }

  /**
   * La frappe a-t-elle été jouée depuis le dernier saut de la tête, et non
   * écrite lors d'un passage précédent qu'on relit ?
   */
  private playedThisPass(lane: Lane, playedNote: PlayedNote): boolean {
    return playedNote.writeOrder !== undefined && playedNote.writeOrder >= (this.passStart.get(lane) ?? 0);
  }

  /** Push one lane's visible notes into the per-frame entries buffer. */
  /**
   * Les notes de cette lane qui sonnent encore, pour chaque pédale en vue.
   *
   * Relevé à part de `collectLaneNotes`, et sur une fenêtre plus large, parce
   * qu'une traîne vit plus longtemps que sa note : une basse frappée bien
   * avant le bas de l'écran résonne encore dans la fenêtre, et son rectangle,
   * lui, est sorti depuis longtemps. Prendre les entrées visibles aurait fait
   * disparaître la traîne avec la note — c'est-à-dire précisément au moment
   * où elle est seule à dire que ça sonne.
   */
  private collectTrails(
    lane: Lane,
    laneIdx: number,
    spans: readonly SustainSpan[],
    fromTick: number,
    toTick: number,
    ticksPerSecond: number,
    out: PedalTrail[],
  ): void {
    if (ticksPerSecond <= 0) {
      return;
    }

    const reach = LONGEST_RING_SECONDS * ticksPerSecond;

    for (const span of spans) {
      const lo = Math.max(span.from, fromTick - reach);
      const hi = Math.min(span.to, toTick);

      if (hi < lo) {
        continue;
      }

      const ringing = this.ringingByMidi;
      ringing.clear();

      for (const played of lane.getNotesInRange(lo, hi)) {
        const midi = played.note.toMidi();
        const ringTicks = ringSeconds(midi) * ticksPerSecond;

        // La même note reprise coupe ce qui sonnait encore : c'est la même
        // corde, et le marteau la reprend. Le synthé fait exactement ça —
        // `SalamanderPianoSynth.noteOn` relâche l'instance précédente de la
        // note —, et sans cette coupe deux traînes se superposaient sur une
        // seule corde, ce qu'aucun piano ne peut produire.
        const open = ringing.get(midi);

        if (open !== undefined) {
          const trail = out[open];

          if (played.tick < trail.toTick) {
            trail.toTick = played.tick;
            trail.covered = ringTicks > 0 ? (played.tick - trail.fromTick) / ringTicks : 0;
          }

          ringing.delete(midi);
        }

        // Une touche encore enfoncée n'a pas de traîne : son rectangle monte
        // déjà jusqu'à la tête de lecture.
        if (played.tickDuration === undefined) {
          continue;
        }

        const noteEndTick = played.tick + played.tickDuration;
        const cut = trailEnd(noteEndTick, span, ringTicks);

        if (cut === null || cut.toTick <= fromTick || noteEndTick >= toTick) {
          continue;
        }

        const index = out.length;
        let trail = this.trailPool[index];

        if (trail === undefined) {
          trail = { midi, laneIdx, fromTick: noteEndTick, toTick: cut.toTick, covered: cut.covered };
          this.trailPool[index] = trail;
        }

        trail.midi = midi;
        trail.laneIdx = laneIdx;
        trail.fromTick = noteEndTick;
        trail.toTick = cut.toTick;
        trail.covered = cut.covered;

        out.push(trail);
        ringing.set(midi, out.length - 1);
      }
    }
  }

  /**
   * Le présent d'une lane : l'instant que « maintenant » y désigne.
   *
   * Pour nos propres gestes, la tête de lecture — c'est là qu'ils s'écrivent.
   * Pour ceux d'un partenaire ou d'un interprète, le présent de la séance :
   * ils portent le tick auquel ils ont été *joués*, un délai de restitution
   * devant notre tête, et notre tête ne les a pas encore atteints.
   *
   * Deux lectures en dépendent, et elles se trompaient toutes les deux en
   * regardant un récital : une note tenue se dessinait à l'envers tant que son
   * note-off n'était pas arrivé, et une pédale enfoncée disparaissait purement
   * — bornée à la tête, sa levée inconnue tombait *avant* son enfoncement, et
   * `getSustainSpans` ne rendait plus rien. Le spectateur ne voyait donc ni
   * gouttière ni traîne tant que l'interprète gardait le pied en bas : tout
   * apparaissait d'un coup à la levée, c'est-à-dire quand ça ne sonnait plus.
   */
  private laneNow(lane: Lane, currentTick: number): number {
    return lane.hasSharedTimebase
      ? Math.max(currentTick, this.localTransport.sourceTick())
      : currentTick;
  }

  /**
   * `offsetTicks` est le décalage d'affichage d'un morceau hors scène : la
   * fenêtre se lit dans les ticks de la lane, et chaque note se dessine dans
   * ceux du rouleau. C'est ici, et seulement ici, que le décalage entre — nul
   * pour tout ce qui est en scène, où l'entrée porte le tick de sa note.
   */
  private collectLaneNotes(
    lane: Lane,
    laneIdx: number,
    fromTick: number,
    toTick: number,
    currentTick: number,
    entries: NoteEntry[],
    offsetTicks = 0,
  ): void {
    const notes = lane.getNotesInRange(fromTick - offsetTicks, toTick - offsetTicks);

    // Une note encore tenue s'étire jusqu'au présent de sa lane. Voir
    // `laneNow` : à la tête pour nos gestes, au présent de la séance pour
    // ceux d'un partenaire.
    const heldUntil = this.laneNow(lane, currentTick);

    for (const playedNote of notes) {
      const tick = playedNote.tick + offsetTicks;
      const noteEndTick = playedNote.tickDuration !== undefined
        ? tick + playedNote.tickDuration
        : heldUntil;

      if (noteEndTick < fromTick || tick >= toTick) {
        continue;
      }

      const index = entries.length;
      let entry = this.entryPool[index];

      if (entry === undefined) {
        entry = { playedNote, laneIdx, tick, noteEndTick, finger: playedNote.finger, held: false };
        this.entryPool[index] = entry;
      }

      entry.playedNote = playedNote;
      entry.laneIdx = laneIdx;
      entry.tick = tick;
      entry.noteEndTick = noteEndTick;
      entry.finger = playedNote.finger;
      entry.isHit = false;
      entry.held = playedNote.tickDuration === undefined;
      entries.push(entry);
    }
  }

  /**
   * Resolve a lane's color hex via its `ColorToken`. `default` lanes
   * (orchestral parts beyond LH/RH) cycle through `EXTRA_TRACK_HEX`.
   */
  private resolveLaneHex(lane: Lane): number {
    const token = this.getColorToken(lane);

    if (token !== 'default') {
      return this.palette[token].hex;
    }

    let extraIdx = this.extraColorIdxByLane.get(lane);

    if (extraIdx === undefined) {
      extraIdx = this.extraColorIdxByLane.size % EXTRA_TRACK_HEX.length;
      this.extraColorIdxByLane.set(lane, extraIdx);
    }

    return EXTRA_TRACK_HEX[extraIdx];
  }

  /**
   * Pitch range to follow: the [min, max] MIDI of notes sounding now or
   * starting within {@link FOLLOW_LOOKAHEAD_SEC}. Written into the reused
   * {@link followRange} buffer (no per-frame allocation); returns `false` when
   * no such note exists (the view then holds its position).
   *
   * Reads the per-frame `entriesBuffer` (already filtered to the visible tick
   * window), so this is O(visible notes) with no extra lane query.
   */
  private updateFollowRange(currentTick: number, currentSeconds: number): boolean {
    const timeline = this.localTransport.transport.timeline;
    const lookaheadTick = timeline.secondsToTick(currentSeconds + FOLLOW_LOOKAHEAD_SEC);

    let minMidi = Infinity;
    let maxMidi = -Infinity;

    const entries = this.entriesBuffer;

    for (let i = 0; i < entries.length; i++) {
      const e = entries[i];

      if (e.tick > lookaheadTick || e.noteEndTick < currentTick) {
        continue;
      }

      const midi = e.playedNote.note.toMidi();

      if (midi < minMidi) {
        minMidi = midi;
      }

      if (midi > maxMidi) {
        maxMidi = midi;
      }
    }

    if (minMidi === Infinity) {
      return false;
    }

    this.followRange.min = minMidi;
    this.followRange.max = maxMidi;

    return true;
  }

  // ── Navigation event handlers ──────────────────────────────────

  private setupNavigation(): void {
    this.stage.on("wheel", this.onWheel);
    this.stage.on("pointerdown", this.onPointerDown);
    this.stage.on("pointermove", this.onPointerMove);
    this.stage.on("pointerup", this.onPointerUp);
    this.stage.on("pointerupoutside", this.onPointerUp);
    this.stage.on("pointerout", this.onPointerOut);
  }

  private teardownNavigation(): void {
    this.stage.off("wheel", this.onWheel);
    this.stage.off("pointerdown", this.onPointerDown);
    this.stage.off("pointermove", this.onPointerMove);
    this.stage.off("pointerup", this.onPointerUp);
    this.stage.off("pointerupoutside", this.onPointerUp);
    this.stage.off("pointerout", this.onPointerOut);
  }

  /** Convert a vertical pixel delta to ticks using the current visible range. */
  private pixelsToTicks(deltaPixels: number): number {
    if (this.height <= 0 || this.currentVisibleTickRange <= 0) {
return 0;
}

    return (deltaPixels / this.height) * this.currentVisibleTickRange;
  }

  private onWheel = (e: FederatedWheelEvent): void => {
    // Trackpad horizontal swipe pans pitch; vertical wheel scrolls time.
    // Wheel idiom: scrolling right reveals higher pitches → content moves left.
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
      this.pianoComponent.panBy(-e.deltaX);

      return;
    }

    // Positive deltaY = scroll down = move backward in time (natural scrolling)
    const deltaTicks = this.pixelsToTicks(-e.deltaY);
    this.viewNavigator.handleWheelDelta(deltaTicks);
  };

  private onPointerDown = (e: FederatedPointerEvent): void => {
    // Appuyer dans la règle pose le repère et le prend en main. Le geste de
    // navigation n'est pas armé : glisser depuis la règle déplace le repère,
    // et surtout ne fait pas défiler le temps sous lui — on ne peut pas viser
    // une mesure sur une surface qui bouge.
    if (this.recordZoneEditable && this.isOverRuler(e.global.x)) {
      this.rulerHeld = true;
      this.setRulerExpanded(true);
      this.edgeDragX = e.global.x;
      this.grabRecordEdge(e.global.y);

      return;
    }

    this.isDragging = true;
    this.lastPointerY = e.global.y;
    this.lastPointerX = e.global.x;
    this.viewNavigator.beginGesture();
  };

  private onPointerMove = (e: FederatedPointerEvent): void => {
    if (this.draggingEdge !== null) {
      this.edgeDragX = e.global.x;
      this.setDiscardProgress(e.global.x);
      // La borne suit le doigt en hauteur même sortie de la règle : si l'on
      // rentre, elle est là où l'on s'attend à la retrouver, et non là où on
      // l'avait laissée en franchissant le seuil.
      this.applyEdge(this.snappedTickAt(e.global.y));

      return;
    }

    // Avant le garde de glissé : le survol de la règle se lit aussi — et
    // surtout — quand aucun bouton n'est enfoncé.
    if (e.pointerType === "mouse" && !this.rulerHeld) {
      this.setRulerExpanded(this.isOverRuler(e.global.x));
    }

    if (!this.isDragging) {
return;
}

    // Horizontal drag pans pitch (grab-and-drag: content follows the finger);
    // vertical drag scrolls time.
    const deltaX = e.global.x - this.lastPointerX;
    this.lastPointerX = e.global.x;

    if (deltaX !== 0) {
      this.pianoComponent.panBy(deltaX);
    }

    const deltaY = e.global.y - this.lastPointerY; // drag down = move backward
    this.lastPointerY = e.global.y;
    const deltaTicks = this.pixelsToTicks(deltaY);
    this.viewNavigator.updateOffset(deltaTicks);
  };

  private onPointerUp = (e: FederatedPointerEvent): void => {
    if (this.draggingEdge !== null) {
      if (this.discardProgress >= 1) {
        this.dropEdge();
      }

      this.draggingEdge = null;
      this.discardProgress = 0;
      this.edgeDragX = null;
      this.rulerHeld = false;
      // La borne reste où on l'a lâchée. La règle, elle, ne reste ouverte que
      // sous une souris encore posée dessus.
      this.setRulerExpanded(e.pointerType === "mouse" && this.isOverRuler(e.global.x));
      this.renderer?.invalidate();
      this.lastDrawnTick = NaN;

      return;
    }

    if (!this.isDragging) {
return;
}

    this.isDragging = false;
    this.viewNavigator.endGesture();
  };

  /**
   * Le pointeur a quitté le roll — pour un autre calque, ou pour hors du
   * canvas. La souris ne repassera pas dire qu'elle a quitté la gouttière :
   * sans ceci, sortir par le bord gauche y laisserait les numéros allumés.
   */
  private onPointerOut = (): void => {
    if (this.rulerHeld) {
      return;
    }

    this.setRulerExpanded(false);
  };

  /**
   * Le pointeur est-il sur la règle ? Sur toute la hauteur du roll, à gauche.
   *
   * Le bord dépend de ce qui est déjà ouvert, et il le faut : la zone qui
   * ouvre la règle est plus étroite que le panneau qu'elle déploie, si bien
   * qu'un curseur immobile à 45 px l'aurait vue s'ouvrir puis se refermer sous
   * lui. Ouverte, c'est son propre bord qui la retient.
   */
  private isOverRuler(globalX: number): boolean {
    const edge = this.rulerExpanded ? RULER_OPEN_WIDTH : RULER_HIT_WIDTH;

    return globalX >= 0 && globalX < edge;
  }

  /**
   * Ouvrir ou refermer la règle.
   *
   * Rien de ce que les deux garde-fous observent n'a bougé — ni le tick, ni la
   * taille, ni l'offset — et le ticker est parqué dès que le transport est en
   * pause, c'est-à-dire précisément quand on va lire la règle. Il faut donc
   * forcer l'image, des deux côtés : celui du composant et celui du rendu.
   */
  private setRulerExpanded(expanded: boolean): void {
    if (expanded === this.rulerExpanded) {
      return;
    }

    this.rulerExpanded = expanded;
    this.lastDrawnTick = NaN;
    this.renderer?.invalidate();
  }

  /**
   * Prendre une borne en main, à la hauteur de l'appui.
   *
   * Trois cas, et ils se lisent dans cet ordre. Rien de posé : c'est le début,
   * et le glissé qui suit l'amène où l'on veut. Un début seul : l'appui pose la
   * fin — sauf s'il tombe sur la capsule du début, qu'on reprend alors plutôt
   * que d'ouvrir une zone qu'on ne voulait pas. Les deux posées : c'est la plus
   * proche qui suit le doigt, ce qui rend chaque geste réversible par le geste
   * inverse.
   */
  private grabRecordEdge(globalY: number): void {
    const tick = this.snappedTickAt(globalY);

    if (tick === null) {
      return;
    }

    if (this.recordStartTick === null) {
      this.draggingEdge = 'start';
    } else if (this.recordEndTick === null) {
      this.draggingEdge = this.isOverEdge(globalY, this.recordStartTick) ? 'start' : 'end';
    } else {
      this.draggingEdge = Math.abs(tick - this.recordStartTick) <= Math.abs(tick - this.recordEndTick)
        ? 'start'
        : 'end';
    }

    this.applyEdge(tick);
  }

  /**
   * Poser la borne en main sur ce tick, et remettre les deux dans l'ordre.
   *
   * Le croisement n'est pas une erreur à interdire : on tire la fin sous le
   * début parce qu'on a changé d'avis sur ce qui commence. Les deux échangent
   * alors leurs valeurs *et* leurs rôles, si bien que le doigt continue de
   * tenir la même ligne — c'est le nom qu'elle porte qui change.
   */
  private applyEdge(tick: number | null): void {
    if (tick === null || this.draggingEdge === null) {
      return;
    }

    if (this.draggingEdge === 'start') {
      this.recordStartTick = tick;
    } else {
      this.recordEndTick = tick;
    }

    if (
      this.recordStartTick !== null
      && this.recordEndTick !== null
      && this.recordStartTick > this.recordEndTick
    ) {
      const swap = this.recordStartTick;
      this.recordStartTick = this.recordEndTick;
      this.recordEndTick = swap;
      this.draggingEdge = this.draggingEdge === 'start' ? 'end' : 'start';
    }

    this.invalidateRecordZone();
  }

  /**
   * Jeter la borne en main.
   *
   * Jeter la fin laisse le début seul, état que le premier appui produit déjà.
   * Jeter le début promeut la fin à sa place : une borne seule *est* un début —
   * c'est ce que dit le modèle, où la fin ne peut pas exister sans lui — et la
   * garder là où le joueur l'avait posée vaut mieux que la faire disparaître
   * avec l'autre.
   */
  private dropEdge(): void {
    if (this.draggingEdge === 'end') {
      this.recordEndTick = null;
    } else {
      this.recordStartTick = this.recordEndTick;
      this.recordEndTick = null;
    }

    this.invalidateRecordZone();
  }

  /**
   * Où la borne en est de sa sortie, d'après l'abscisse du pointeur.
   *
   * La pente part de la place de la capsule — on tire, ça pâlit — et finit au
   * point de non-retour. Bornée à 1 : au-delà, rien ne change, on est déjà
   * dehors.
   */
  private setDiscardProgress(globalX: number): void {
    const from = RULER_OPEN_WIDTH - RECORD_HANDLE_INSET;
    const to = this.discardThreshold();
    const progress = to <= from
      ? 0
      : Math.min(1, Math.max(0, (globalX - from) / (to - from)));

    if (progress === this.discardProgress) {
      return;
    }

    this.discardProgress = progress;
    this.lastDrawnTick = NaN;
    this.renderer?.invalidate();
  }

  /**
   * Le point de non-retour : au-delà, relâcher jette la borne.
   *
   * Loin du panneau, parce qu'un glissé vertical dérive latéralement sans
   * qu'on le veuille et que jeter par accident ce qu'on venait de placer est
   * le pire des ratés. Mais jamais au-delà de la moitié du roll : sur un
   * écran étroit, un seuil absolu deviendrait inatteignable au pouce.
   */
  private discardThreshold(): number {
    // Le plancher n'est pas décoratif : sur un roll très étroit, la moitié de
    // sa largeur tomberait *dans* le panneau ouvert, et le simple fait d'y
    // appuyer jetterait la borne qu'on vient d'y poser.
    return Math.max(
      RULER_OPEN_WIDTH + EDGE_GRAB_PX,
      Math.min(RULER_OPEN_WIDTH + DISCARD_MARGIN_PX, this.width / 2),
    );
  }

  /** L'appui tombe-t-il sur la capsule de cette borne ? */
  private isOverEdge(globalY: number, tick: number): boolean {
    const range = this.lastToTick - this.lastFromTick;

    if (range <= 0 || this.height <= 0) {
      return false;
    }

    const y = this.height - (tick - this.lastFromTick) * (this.height / range);

    return Math.abs(y - globalY) <= EDGE_GRAB_PX;
  }

  /**
   * Le demi-temps sous le pointeur, ou `null` si aucune image n'a encore été
   * dessinée — la fenêtre de ticks vient de là.
   *
   * Le calage se fait ici, sur le tick, et pas au dessin : la borne *est* un
   * demi-temps, c'est ce qui permet à son étiquette de dire « M.5.3½ » sans
   * arrondir quoi que ce soit, et à qui la lira de partir exactement là.
   */
  private snappedTickAt(globalY: number): number | null {
    const range = this.lastToTick - this.lastFromTick;

    if (range <= 0 || this.height <= 0) {
      return null;
    }

    return this.snapToSubdivision(this.lastToTick - (globalY / this.height) * range);
  }

  /**
   * Le demi-temps le plus proche.
   *
   * La plus petite graduation de la règle, et donc le pas du repère : se caler
   * sur autre chose qu'un trait qu'on voit serait un calage invisible.
   *
   * Même hypothèse que `getBarAtTick` et `getBeatAtTick`, dont ceci est
   * l'inverse : une seule métrique pour tout le morceau. C'est déjà celle sur
   * laquelle le studio compte ses mesures partout ailleurs, et un repère qui
   * se calerait sur une autre annoncerait un numéro que personne d'autre
   * n'affiche.
   */
  private snapToSubdivision(tick: number): number {
    const step = this.subdivisionTicks(tick);

    if (step <= 0) {
      return Math.max(0, tick);
    }

    return Math.max(0, Math.round(tick / step) * step);
  }

  /** Le repère est-il posé sur le « et » du temps plutôt que sur le temps ? */
  private isOffBeat(tick: number): boolean {
    const step = this.subdivisionTicks(tick);

    if (step <= 0) {
      return false;
    }

    // Un demi-temps sur deux est un temps : c'est la parité du rang qui le dit,
    // et elle est exacte — le tick a été calé sur ce pas juste avant.
    return Math.round(tick / step) % 2 !== 0;
  }

  /** Le pas du repère, en ticks : la moitié d'un temps. */
  private subdivisionTicks(tick: number): number {
    const timeline = this.localTransport.transport.timeline;

    return timeline.getTicksPerBeat(Math.max(0, tick)) / 2;
  }

  /**
   * Ouvrir ou fermer la pose des bornes dans la règle.
   *
   * Coupée en plein geste, la borne en main est relâchée là où elle est : ni
   * jetée — le geste n'a pas été mené à son terme — ni figée dans un
   * déplacement que plus rien ne conduirait.
   */
  setRecordZoneEditable(editable: boolean): void {
    if (editable === this.recordZoneEditable) {
      return;
    }

    this.recordZoneEditable = editable;

    if (!editable && this.draggingEdge !== null) {
      this.draggingEdge = null;
      this.discardProgress = 0;
      this.edgeDragX = null;
      this.lastDrawnTick = NaN;
      this.renderer?.invalidate();
    }
  }

  get isRecordZoneEditable(): boolean {
    return this.recordZoneEditable;
  }

  /**
   * La zone d'enregistrement, ou `null` tant que rien n'est posé.
   *
   * En lecture pour qui construira l'enregistrement, en écriture pour qui
   * voudra la poser autrement qu'à la main.
   */
  get recordZone(): RecordZone | null {
    if (this.recordStartTick === null) {
      return null;
    }

    return { startTick: this.recordStartTick, endTick: this.recordEndTick };
  }

  setRecordZone(zone: RecordZone | null): void {
    const startTick = zone?.startTick ?? null;
    const endTick = zone === null ? null : zone.endTick;

    if (startTick === this.recordStartTick && endTick === this.recordEndTick) {
      return;
    }

    this.recordStartTick = startTick;
    this.recordEndTick = startTick === null ? null : endTick;
    this.invalidateRecordZone();
  }

  /** S'abonner au déplacement des bornes. Un seul abonné : la vue. */
  onRecordZoneChange(listener: (zone: RecordZone | null) => void): void {
    this.recordZoneListener = listener;
  }

  /**
   * La zone a bougé : forcer l'image et le dire.
   *
   * Rien de ce que les deux garde-fous observent n'a changé, et le ticker est
   * parqué dès que le transport est en pause — c'est-à-dire précisément quand
   * on pose une zone.
   */
  private invalidateRecordZone(): void {
    this.recordRange = this.recordStartTick !== null && this.recordEndTick !== null
      ? { startTick: this.recordStartTick, endTick: this.recordEndTick }
      : null;

    this.lastDrawnTick = NaN;
    this.renderer?.invalidate();
    this.recordZoneListener?.(this.recordZone);
  }

  build(): void {
    this.syncLanes();

    this.unsubscribeTrackManager = this.trackManager.onChange(() => {
      this.syncLanes();
    });
  }

  /**
   * Collect all lanes from all tracks into this.lanes.
   * Also subscribes to per-track onChange for future lane additions.
   */
  private syncLanes(): void {
    for (const unsub of this.trackUnsubscribes) {
      unsub();
    }

    this.trackUnsubscribes = [];

    for (const unsub of this.laneUnsubscribes) {
      unsub();
    }

    this.laneUnsubscribes = [];

    const lanes: Lane[] = [];
    const stableIndices = new Map<Lane, number>();
    let stableIdx = 0;

    for (const track of this.trackManager.getTracks()) {
      for (const lane of track.getLanes()) {
        lanes.push(lane);
        stableIndices.set(lane, stableIdx++);

        const unsub = lane.on((e) => {
          if (e.type === 'clusterChange') {
            this.hintCacheDirty = true;
          }
        });
        this.laneUnsubscribes.push(unsub);
      }

      const unsub = track.onChange(() => {
        this.syncLanesImmediate();
      });
      this.trackUnsubscribes.push(unsub);
    }

    this.lanes = lanes;
    this.laneStableIndices = stableIndices;
    this.syncOffstage();
    this.pruneExtraColorMap();
    this.hintCacheDirty = true;
  }

  /**
   * Indexe les lanes des morceaux hors scène à la suite de celles en scène.
   *
   * Refait quand l'une ou l'autre liste change : les indices en scène
   * viennent du gestionnaire de pistes, ceux-ci s'y ajoutent, et les tampons
   * de couleur les tiennent tous par indice.
   */
  private syncOffstage(): void {
    const indices = new Map<Lane, number>();
    let next = this.laneStableIndices.size;

    for (const song of this.sequence.offstage()) {
      for (const lane of song.lanes) {
        indices.set(lane, next++);
      }
    }

    this.offstageIndices = indices;
  }

  /**
   * La séquence a changé — un morceau ajouté, retiré, la tête passée dans un
   * autre. Rien de ce que les garde-fous observent n'a forcément bougé : un
   * morceau ajouté derrière le courant, rouleau à l'arrêt, n'apparaîtrait
   * qu'au prochain geste.
   */
  sequenceChanged(): void {
    this.syncOffstage();
    this.pruneExtraColorMap();
    this.lastDrawnTick = NaN;
    this.renderer?.invalidate();
  }

  private pruneExtraColorMap(): void {
    const present = new Set(this.lanes);

    for (const lane of this.offstageIndices.keys()) {
      present.add(lane);
    }

    for (const lane of [...this.extraColorIdxByLane.keys()]) {
      if (!present.has(lane)) {
        this.extraColorIdxByLane.delete(lane);
      }
    }
  }

  /**
   * Merge clusters from all lanes into a single unified row list.
   *
   * Clusters at the same tick (same chord across hands) are merged into one row
   * with multiple hands. Clusters at different ticks produce separate rows.
   */
  private computeUnifiedRows(fromTick: number, toTick: number): HintRow[] {
    // Return cached result when the tick window is still covered and lanes
    // haven't changed.  The cache is invalidated (hintCacheDirty = true)
    // whenever lanes are added/removed/changed.
    if (
      !this.hintCacheDirty
      && fromTick >= this.cachedHintFromTick
      && toTick <= this.cachedHintToTick
    ) {
      return this.cachedHintRows;
    }

    // Over-fetch on each side so small scroll movements don't immediately
    // invalidate the cache.
    //
    // Côté passé, la marge est plus large : l'étiquette d'accord reste collée
    // à la playhead toute la durée de l'accord, puis ressort par le bas. Elle
    // a donc encore à être dessinée alors que son tick est loin derrière —
    // d'une durée d'accord, que la fenêtre visible ne borne pas. Une pleine
    // fenêtre de rab couvre les accords longs (rondes lentes) sans quoi
    // l'étiquette s'évanouirait en pleine descente.
    const range = toTick - fromTick;
    const fetchFrom = fromTick - range;
    const fetchTo = toTick + range * 0.5;

    // Collect all clusters from all lanes, tagged with lane index
    type TaggedCluster = { cluster: NoteCluster; laneIdx: number };
    const tagged: TaggedCluster[] = [];

    for (let i = 0; i < this.lanes.length; i++) {
      const lane = this.lanes[i];
      // A hidden lane says nothing, and neither does a trace: its clusters are
      // what the player held, read by a chord detector that would print
      // « C oct » over the score's own harmony row.
      if (lane.isHidden() || this.isTraceLane(lane)) {
continue;
}

      const laneIdx = this.laneStableIndices.get(lane) ?? i;
      for (const cluster of lane.getClustersInRange(fetchFrom, fetchTo)) {
        tagged.push({ cluster, laneIdx });
      }
    }

    // Sort by startTick
    tagged.sort((a, b) => a.cluster.startTick - b.cluster.startTick);

    // Group clusters that share the same startTick into one row
    const rows: HintRow[] = [];
    let i = 0;

    while (i < tagged.length) {
      const tick = tagged[i].cluster.startTick;

      // Collect all clusters at the same tick
      const group: TaggedCluster[] = [];
      while (i < tagged.length && tagged[i].cluster.startTick === tick) {
        group.push(tagged[i]);
        i++;
      }

      rows.push(this.buildRow(group));
    }

    // Truncate each hint at the start of the next to prevent overlap
    const GAP_TICKS = 10;
    for (let r = 0; r < rows.length - 1; r++) {
      rows[r].endTick = Math.min(rows[r].endTick, rows[r + 1].tick - GAP_TICKS);
    }

    // Store in cache
    this.cachedHintRows = rows;
    this.cachedHintFromTick = fetchFrom;
    this.cachedHintToTick = fetchTo;
    this.hintCacheDirty = false;

    return rows;
  }

  /**
   * Build a single HintRow from a group of clusters at the same tick.
   */
  private buildRow(group: { cluster: NoteCluster; laneIdx: number }[]): HintRow {
    // Use the widest time span
    let endTick = 0;
    let maxOnsetTick = 0;

    const hands: HandNotes[] = [];
    const allMidis = new Set<number>();

    // Pick chord info from the first cluster that has one
    const chordNotation = getNotationStore().chordNotation.value;
    let chordName: string | undefined;
    let chordRoot: number | undefined;
    let chordEndTick: number | undefined;
    let romanNumeral: string | undefined;
    let voicingType: string | undefined;
    let hasChord = false;

    for (const { cluster, laneIdx } of group) {
      if (cluster.endTick > endTick) {
endTick = cluster.endTick;
}

      const midis = [...new Set(cluster.notes.map(n => n.note.toMidi()))].sort((a, b) => a - b);
      for (const m of midis) {
allMidis.add(m);
}

      const clusterMaxOnset = cluster.notes.length > 0
        ? Math.max(...cluster.notes.map(n => n.tick))
        : cluster.startTick;
      if (clusterMaxOnset > maxOnsetTick) {
maxOnsetTick = clusterMaxOnset;
}

      // Build hand info with harmony-aware degrees
      const handInfo = this.buildHandInfo(cluster.annotatedNotes ?? []);
      hands.push({ laneIdx, midis, ...handInfo });

      // Take chord info from first cluster that has it.
      // Prefer analyzer harmony (shared reference), fall back to local detection (live piano).
      if (!hasChord) {
        if (cluster.harmony) {
          chordName = chordSymbolLabel(cluster.harmony.symbol, chordNotation);
          chordRoot = cluster.harmony.rootPitchClass;
          // La zone de l'accord, prise à la source : `endTick` ci-dessous est
          // celui de la grappe, et sera tronqué à la rangée suivante.
          chordEndTick = cluster.harmony.endTick;
          romanNumeral = cluster.harmony.romanNumeral;
          voicingType = cluster.harmony.voicing?.type;
          hasChord = true;
        } else if (cluster.chord) {
          chordName = chordSymbolLabel(cluster.chord.name, chordNotation);
          chordRoot = cluster.chord.root;
          hasChord = true;
        }
      }
    }

    const sortedMidis = [...allMidis].sort((a, b) => a - b);
    const pitchClasses = [...new Set(sortedMidis.map(m => m % 12))];

    const type: HintRow["type"] = hasChord
      ? "chord"
      : sortedMidis.length === 1
        ? "single"
        : "multi";

    return {
      tick: group[0].cluster.startTick,
      maxOnsetTick,
      endTick,
      type,
      pitchClasses,
      midis: sortedMidis,
      hands,
      chordName,
      chordRoot,
      chordEndTick,
      romanNumeral,
      voicingType,
    };
  }

  /**
   * Build hint labels for a hand from its annotated notes.
   *
   * Chord tones are shown as degree labels (1, ♭3, 5).
   * Non-chord tones are shown as note names (D5, E5) with muted style.
   * All labels are sorted by MIDI pitch (low→high) and deduplicated.
   */
  private buildHandInfo(annotatedNotes: AnnotatedNote[]): {
    labels: HintLabel[];
  } {
    const notation = getNotationStore().noteNotation.value;
    // Sort by MIDI pitch ascending
    const sorted = [...annotatedNotes].sort(
      (a, b) => a.note.toMidi() - b.note.toMidi(),
    );

    // Build labels in pitch order
    const labels: HintLabel[] = [];
    const seen = new Set<string>();

    for (const n of sorted) {
      if (!n.harmony) {
continue;
}

      const isNonChord = n.harmony.tone === 'non-chord';
      const text = noteLabel(pitchNameWithOctave(n.note.toMidi()), notation);

      if (seen.has(text)) {
continue;
}

      seen.add(text);

      labels.push({
        text,
        style: isNonChord ? 'muted' : 'bright',
      });
    }

    return { labels };
  }



  /**
   * Immediate re-sync (when lanes are added/removed within a track).
   */
  private syncLanesImmediate(): void {
    for (const unsub of this.laneUnsubscribes) {
      unsub();
    }

    this.laneUnsubscribes = [];

    const lanes: Lane[] = [];
    const stableIndices = new Map<Lane, number>();
    let stableIdx = 0;

    for (const track of this.trackManager.getTracks()) {
      for (const lane of track.getLanes()) {
        lanes.push(lane);
        stableIndices.set(lane, stableIdx++);

        const unsub = lane.on((e) => {
          if (e.type === 'clusterChange') {
            this.hintCacheDirty = true;
          }
        });
        this.laneUnsubscribes.push(unsub);
      }
    }

    this.lanes = lanes;
    this.laneStableIndices = stableIndices;
    this.syncOffstage();
    this.pruneExtraColorMap();
    this.hintCacheDirty = true;
  }

  /**
   * Drops the cached hint rows so the next frame rebuilds their labels.
   * Called when the note-name notation changes — the labels are baked into
   * the rows at build time, so nothing short of a rebuild picks up the new
   * spelling.
   */
  /** The key spans to draw on the right edge; `[]` clears them. */
  setKeySpans(spans: readonly KeySpan[]): void {
    this.keySpans = spans;
  }

  invalidateHintLabels(): void {
    this.hintCacheDirty = true;
  }

  /**
   * Redessiner après qu'une piste a perdu ses notes.
   *
   * Vider une piste ne bouge rien de ce que le garde-fou statique compare —
   * ni le tick, ni la taille, ni le nombre de pistes visibles — donc le roll
   * continuerait de présenter l'image d'avant, et le tracé effacé ne
   * disparaîtrait qu'au premier scroll. Le renderer tient son propre
   * garde-fou, d'où les deux.
   */
  invalidateNotes(): void {
    this.lastDrawnTick = NaN;
    this.renderer?.invalidate();
    this.hintCacheDirty = true;
  }

  async destroy(): Promise<void> {
    this.teardownNavigation();
    this.unsubscribeTrackManager?.();
    this.unsubscribePedal?.();
    this.unsubscribeTransport?.();

    for (const unsub of this.trackUnsubscribes) {
      unsub();
    }

    this.trackUnsubscribes = [];

    for (const unsub of this.laneUnsubscribes) {
      unsub();
    }

    this.laneUnsubscribes = [];

    this.renderer?.destroy();
    this.renderer = undefined;
  }
}
